// ============================================
// PERMISSIONS & ACCESS CONTROL MODEL
// Role-based access for franchise management
// ============================================

// ============================================
// ROLE DEFINITIONS
// ============================================

export const SystemRole = {
  // Super admin - full system access
  SUPER_ADMIN: 'super_admin',
  
  // Corporate/Group level roles
  CORPORATE_ADMIN: 'corporate_admin',       // Full cross-franchise visibility
  CORPORATE_FINANCE: 'corporate_finance',   // Financial reports across all
  CORPORATE_OPERATIONS: 'corporate_ops',    // Operations across all
  
  // Franchise level roles (scoped to franchisee)
  FRANCHISE_OWNER: 'franchise_owner',       // Full access to own franchisee
  FRANCHISE_ADMIN: 'franchise_admin',       // Admin for own franchisee
  FRANCHISE_FINANCE: 'franchise_finance',   // Finance for own franchisee
  
  // Outlet level roles (scoped to specific outlets)
  OUTLET_MANAGER: 'outlet_manager',         // Full access to assigned outlets
  OUTLET_SUPERVISOR: 'outlet_supervisor',   // Supervisory access
  OUTLET_STAFF: 'outlet_staff',             // Basic POS access
};

export const RoleHierarchy = {
  [SystemRole.SUPER_ADMIN]: 100,
  [SystemRole.CORPORATE_ADMIN]: 90,
  [SystemRole.CORPORATE_FINANCE]: 80,
  [SystemRole.CORPORATE_OPERATIONS]: 80,
  [SystemRole.FRANCHISE_OWNER]: 70,
  [SystemRole.FRANCHISE_ADMIN]: 60,
  [SystemRole.FRANCHISE_FINANCE]: 50,
  [SystemRole.OUTLET_MANAGER]: 40,
  [SystemRole.OUTLET_SUPERVISOR]: 30,
  [SystemRole.OUTLET_STAFF]: 10,
};

// ============================================
// PERMISSION DEFINITIONS
// ============================================

export const Permission = {
  // Franchise management
  FRANCHISE_VIEW_ALL: 'franchise:view:all',
  FRANCHISE_VIEW_OWN: 'franchise:view:own',
  FRANCHISE_CREATE: 'franchise:create',
  FRANCHISE_UPDATE: 'franchise:update',
  FRANCHISE_DELETE: 'franchise:delete',
  
  // Outlet management
  OUTLET_VIEW_ALL: 'outlet:view:all',
  OUTLET_VIEW_FRANCHISE: 'outlet:view:franchise',  // Own franchisee's outlets
  OUTLET_VIEW_ASSIGNED: 'outlet:view:assigned',    // Only assigned outlets
  OUTLET_CREATE: 'outlet:create',
  OUTLET_UPDATE: 'outlet:update',
  OUTLET_DELETE: 'outlet:delete',
  
  // Financial
  FINANCE_VIEW_ALL: 'finance:view:all',
  FINANCE_VIEW_FRANCHISE: 'finance:view:franchise',
  FINANCE_VIEW_OUTLET: 'finance:view:outlet',
  FINANCE_REPORTS_ALL: 'finance:reports:all',
  FINANCE_REPORTS_FRANCHISE: 'finance:reports:franchise',
  FINANCE_APPROVE_EXPENSES: 'finance:approve:expenses',
  
  // Pricing
  PRICING_VIEW: 'pricing:view',
  PRICING_SET_GLOBAL: 'pricing:set:global',
  PRICING_SET_FRANCHISE: 'pricing:set:franchise',
  PRICING_SET_OUTLET: 'pricing:set:outlet',
  
  // VAT configuration
  VAT_VIEW: 'vat:view',
  VAT_CONFIGURE: 'vat:configure',
  
  // Royalty/Fees
  FEES_VIEW_ALL: 'fees:view:all',
  FEES_VIEW_OWN: 'fees:view:own',
  FEES_CONFIGURE: 'fees:configure',
  FEES_APPROVE: 'fees:approve',
  
  // Users
  USER_VIEW_ALL: 'user:view:all',
  USER_VIEW_FRANCHISE: 'user:view:franchise',
  USER_VIEW_OUTLET: 'user:view:outlet',
  USER_CREATE: 'user:create',
  USER_UPDATE: 'user:update',
  USER_DELETE: 'user:delete',
  USER_ASSIGN_ROLE: 'user:assign:role',
  
  // POS
  POS_ACCESS: 'pos:access',
  POS_VOID_ORDER: 'pos:void:order',
  POS_DISCOUNT: 'pos:discount',
  POS_REFUND: 'pos:refund',
  
  // Inventory
  INVENTORY_VIEW: 'inventory:view',
  INVENTORY_MANAGE: 'inventory:manage',
  INVENTORY_ORDER: 'inventory:order',
  
  // Reports
  REPORTS_VIEW_ALL: 'reports:view:all',
  REPORTS_VIEW_FRANCHISE: 'reports:view:franchise',
  REPORTS_VIEW_OUTLET: 'reports:view:outlet',
  REPORTS_EXPORT: 'reports:export',
  
  // Settings
  SETTINGS_VIEW: 'settings:view',
  SETTINGS_GLOBAL: 'settings:global',
  SETTINGS_FRANCHISE: 'settings:franchise',
  SETTINGS_OUTLET: 'settings:outlet',
  
  // Audit
  AUDIT_VIEW_ALL: 'audit:view:all',
  AUDIT_VIEW_FRANCHISE: 'audit:view:franchise',
  AUDIT_VIEW_OUTLET: 'audit:view:outlet',
};

// ============================================
// ROLE-PERMISSION MAPPING
// ============================================

export const RolePermissions = {
  [SystemRole.SUPER_ADMIN]: Object.values(Permission), // All permissions
  
  [SystemRole.CORPORATE_ADMIN]: [
    // Franchise
    Permission.FRANCHISE_VIEW_ALL,
    Permission.FRANCHISE_CREATE,
    Permission.FRANCHISE_UPDATE,
    // Outlets
    Permission.OUTLET_VIEW_ALL,
    Permission.OUTLET_CREATE,
    Permission.OUTLET_UPDATE,
    Permission.OUTLET_DELETE,
    // Finance
    Permission.FINANCE_VIEW_ALL,
    Permission.FINANCE_REPORTS_ALL,
    Permission.FINANCE_APPROVE_EXPENSES,
    // Pricing & VAT
    Permission.PRICING_VIEW,
    Permission.PRICING_SET_GLOBAL,
    Permission.PRICING_SET_FRANCHISE,
    Permission.VAT_VIEW,
    Permission.VAT_CONFIGURE,
    // Fees
    Permission.FEES_VIEW_ALL,
    Permission.FEES_CONFIGURE,
    Permission.FEES_APPROVE,
    // Users
    Permission.USER_VIEW_ALL,
    Permission.USER_CREATE,
    Permission.USER_UPDATE,
    Permission.USER_DELETE,
    Permission.USER_ASSIGN_ROLE,
    // POS
    Permission.POS_ACCESS,
    Permission.POS_VOID_ORDER,
    Permission.POS_DISCOUNT,
    Permission.POS_REFUND,
    // Inventory
    Permission.INVENTORY_VIEW,
    Permission.INVENTORY_MANAGE,
    Permission.INVENTORY_ORDER,
    // Reports
    Permission.REPORTS_VIEW_ALL,
    Permission.REPORTS_EXPORT,
    // Settings
    Permission.SETTINGS_VIEW,
    Permission.SETTINGS_GLOBAL,
    Permission.SETTINGS_FRANCHISE,
    Permission.SETTINGS_OUTLET,
    // Audit
    Permission.AUDIT_VIEW_ALL,
  ],
  
  [SystemRole.CORPORATE_FINANCE]: [
    Permission.FRANCHISE_VIEW_ALL,
    Permission.OUTLET_VIEW_ALL,
    Permission.FINANCE_VIEW_ALL,
    Permission.FINANCE_REPORTS_ALL,
    Permission.FINANCE_APPROVE_EXPENSES,
    Permission.PRICING_VIEW,
    Permission.VAT_VIEW,
    Permission.FEES_VIEW_ALL,
    Permission.FEES_APPROVE,
    Permission.REPORTS_VIEW_ALL,
    Permission.REPORTS_EXPORT,
    Permission.AUDIT_VIEW_ALL,
  ],
  
  [SystemRole.CORPORATE_OPERATIONS]: [
    Permission.FRANCHISE_VIEW_ALL,
    Permission.OUTLET_VIEW_ALL,
    Permission.OUTLET_UPDATE,
    Permission.FINANCE_VIEW_ALL,
    Permission.PRICING_VIEW,
    Permission.VAT_VIEW,
    Permission.POS_ACCESS,
    Permission.POS_VOID_ORDER,
    Permission.INVENTORY_VIEW,
    Permission.INVENTORY_MANAGE,
    Permission.REPORTS_VIEW_ALL,
    Permission.SETTINGS_VIEW,
    Permission.AUDIT_VIEW_ALL,
  ],
  
  [SystemRole.FRANCHISE_OWNER]: [
    Permission.FRANCHISE_VIEW_OWN,
    Permission.OUTLET_VIEW_FRANCHISE,
    Permission.OUTLET_UPDATE,
    Permission.FINANCE_VIEW_FRANCHISE,
    Permission.FINANCE_REPORTS_FRANCHISE,
    Permission.FINANCE_APPROVE_EXPENSES,
    Permission.PRICING_VIEW,
    Permission.PRICING_SET_FRANCHISE,
    Permission.PRICING_SET_OUTLET,
    Permission.VAT_VIEW,
    Permission.FEES_VIEW_OWN,
    Permission.USER_VIEW_FRANCHISE,
    Permission.USER_CREATE,
    Permission.USER_UPDATE,
    Permission.POS_ACCESS,
    Permission.POS_VOID_ORDER,
    Permission.POS_DISCOUNT,
    Permission.POS_REFUND,
    Permission.INVENTORY_VIEW,
    Permission.INVENTORY_MANAGE,
    Permission.INVENTORY_ORDER,
    Permission.REPORTS_VIEW_FRANCHISE,
    Permission.REPORTS_EXPORT,
    Permission.SETTINGS_VIEW,
    Permission.SETTINGS_FRANCHISE,
    Permission.SETTINGS_OUTLET,
    Permission.AUDIT_VIEW_FRANCHISE,
  ],
  
  [SystemRole.FRANCHISE_ADMIN]: [
    Permission.FRANCHISE_VIEW_OWN,
    Permission.OUTLET_VIEW_FRANCHISE,
    Permission.OUTLET_UPDATE,
    Permission.FINANCE_VIEW_FRANCHISE,
    Permission.FINANCE_REPORTS_FRANCHISE,
    Permission.PRICING_VIEW,
    Permission.PRICING_SET_OUTLET,
    Permission.VAT_VIEW,
    Permission.FEES_VIEW_OWN,
    Permission.USER_VIEW_FRANCHISE,
    Permission.USER_CREATE,
    Permission.USER_UPDATE,
    Permission.POS_ACCESS,
    Permission.POS_VOID_ORDER,
    Permission.POS_DISCOUNT,
    Permission.INVENTORY_VIEW,
    Permission.INVENTORY_MANAGE,
    Permission.INVENTORY_ORDER,
    Permission.REPORTS_VIEW_FRANCHISE,
    Permission.SETTINGS_VIEW,
    Permission.SETTINGS_OUTLET,
    Permission.AUDIT_VIEW_FRANCHISE,
  ],
  
  [SystemRole.FRANCHISE_FINANCE]: [
    Permission.FRANCHISE_VIEW_OWN,
    Permission.OUTLET_VIEW_FRANCHISE,
    Permission.FINANCE_VIEW_FRANCHISE,
    Permission.FINANCE_REPORTS_FRANCHISE,
    Permission.FINANCE_APPROVE_EXPENSES,
    Permission.PRICING_VIEW,
    Permission.VAT_VIEW,
    Permission.FEES_VIEW_OWN,
    Permission.REPORTS_VIEW_FRANCHISE,
    Permission.REPORTS_EXPORT,
    Permission.AUDIT_VIEW_FRANCHISE,
  ],
  
  [SystemRole.OUTLET_MANAGER]: [
    Permission.OUTLET_VIEW_ASSIGNED,
    Permission.OUTLET_UPDATE,
    Permission.FINANCE_VIEW_OUTLET,
    Permission.PRICING_VIEW,
    Permission.VAT_VIEW,
    Permission.USER_VIEW_OUTLET,
    Permission.USER_CREATE,
    Permission.USER_UPDATE,
    Permission.POS_ACCESS,
    Permission.POS_VOID_ORDER,
    Permission.POS_DISCOUNT,
    Permission.POS_REFUND,
    Permission.INVENTORY_VIEW,
    Permission.INVENTORY_MANAGE,
    Permission.INVENTORY_ORDER,
    Permission.REPORTS_VIEW_OUTLET,
    Permission.SETTINGS_VIEW,
    Permission.SETTINGS_OUTLET,
    Permission.AUDIT_VIEW_OUTLET,
  ],
  
  [SystemRole.OUTLET_SUPERVISOR]: [
    Permission.OUTLET_VIEW_ASSIGNED,
    Permission.FINANCE_VIEW_OUTLET,
    Permission.PRICING_VIEW,
    Permission.USER_VIEW_OUTLET,
    Permission.POS_ACCESS,
    Permission.POS_VOID_ORDER,
    Permission.POS_DISCOUNT,
    Permission.INVENTORY_VIEW,
    Permission.REPORTS_VIEW_OUTLET,
    Permission.AUDIT_VIEW_OUTLET,
  ],
  
  [SystemRole.OUTLET_STAFF]: [
    Permission.OUTLET_VIEW_ASSIGNED,
    Permission.POS_ACCESS,
    Permission.INVENTORY_VIEW,
  ],
};

// ============================================
// USER SCHEMA WITH FRANCHISE SCOPING
// ============================================

export const UserSchema = {
  id: 'string',
  email: 'string',
  name: 'string',
  
  // Authentication
  authProvider: 'string',          // google, email, etc.
  authProviderId: 'string',
  
  // Role assignment
  role: 'SystemRole',
  
  // Scope - determines what the user can access
  scope: {
    type: 'string',                // global, franchise, outlet
    franchiseGroupId: 'string',    // null for global scope
    franchiseeId: 'string',        // null for corporate/global
    outletIds: ['string'],         // specific outlets (for outlet-level roles)
  },
  
  // Additional permissions (beyond role)
  additionalPermissions: ['Permission'],
  
  // Denied permissions (override role permissions)
  deniedPermissions: ['Permission'],
  
  // Status
  isActive: 'boolean',
  lastLogin: 'datetime',
  
  // Metadata
  createdAt: 'datetime',
  updatedAt: 'datetime',
};

// ============================================
// ACCESS CONTROL FUNCTIONS
// ============================================

/**
 * Check if user has a specific permission
 */
export function hasPermission(user, permission) {
  if (!user || !user.role) return false;
  
  // Check denied permissions first
  if (user.deniedPermissions?.includes(permission)) {
    return false;
  }
  
  // Check additional permissions
  if (user.additionalPermissions?.includes(permission)) {
    return true;
  }
  
  // Check role permissions
  const rolePerms = RolePermissions[user.role] || [];
  return rolePerms.includes(permission);
}

/**
 * Check if user can access a specific franchisee's data
 */
export function canAccessFranchisee(user, franchiseeId) {
  if (!user) return false;
  
  // Global/corporate scope can access all
  if (user.scope?.type === 'global') return true;
  if (hasPermission(user, Permission.FRANCHISE_VIEW_ALL)) return true;
  
  // Franchise scope - check if same franchisee
  if (user.scope?.type === 'franchise') {
    return user.scope.franchiseeId === franchiseeId;
  }
  
  // Outlet scope - need to check if outlet belongs to franchisee
  // This would require outlet lookup in real implementation
  return false;
}

/**
 * Check if user can access a specific outlet
 */
export function canAccessOutlet(user, outletId, outlet) {
  if (!user) return false;
  
  // Global/corporate scope can access all
  if (user.scope?.type === 'global') return true;
  if (hasPermission(user, Permission.OUTLET_VIEW_ALL)) return true;
  
  // Franchise scope - check if outlet belongs to user's franchisee
  if (user.scope?.type === 'franchise') {
    if (hasPermission(user, Permission.OUTLET_VIEW_FRANCHISE)) {
      return outlet?.franchiseeId === user.scope.franchiseeId;
    }
  }
  
  // Outlet scope - check if outlet is in user's assigned outlets
  if (user.scope?.type === 'outlet') {
    if (hasPermission(user, Permission.OUTLET_VIEW_ASSIGNED)) {
      return user.scope.outletIds?.includes(outletId);
    }
  }
  
  return false;
}

/**
 * Filter outlets based on user access
 */
export function filterAccessibleOutlets(user, outlets) {
  if (!user || !outlets) return [];
  
  // Global access
  if (user.scope?.type === 'global' || hasPermission(user, Permission.OUTLET_VIEW_ALL)) {
    return outlets;
  }
  
  // Franchise scope
  if (user.scope?.type === 'franchise' && hasPermission(user, Permission.OUTLET_VIEW_FRANCHISE)) {
    return outlets.filter(o => o.franchiseeId === user.scope.franchiseeId);
  }
  
  // Outlet scope
  if (user.scope?.type === 'outlet' && hasPermission(user, Permission.OUTLET_VIEW_ASSIGNED)) {
    return outlets.filter(o => user.scope.outletIds?.includes(o.id));
  }
  
  return [];
}

/**
 * Filter franchisees based on user access
 */
export function filterAccessibleFranchisees(user, franchisees) {
  if (!user || !franchisees) return [];
  
  // Global access
  if (user.scope?.type === 'global' || hasPermission(user, Permission.FRANCHISE_VIEW_ALL)) {
    return franchisees;
  }
  
  // Franchise scope - only own franchisee
  if (user.scope?.type === 'franchise' && hasPermission(user, Permission.FRANCHISE_VIEW_OWN)) {
    return franchisees.filter(f => f.id === user.scope.franchiseeId);
  }
  
  return [];
}

/**
 * Get data visibility level for user
 */
export function getDataVisibility(user) {
  if (!user) return 'none';
  
  if (user.scope?.type === 'global' || hasPermission(user, Permission.FRANCHISE_VIEW_ALL)) {
    return 'all';
  }
  
  if (user.scope?.type === 'franchise') {
    return 'franchise';
  }
  
  if (user.scope?.type === 'outlet') {
    return 'outlet';
  }
  
  return 'none';
}

/**
 * Check if user is corporate level (cross-franchise visibility)
 */
export function isCorporateUser(user) {
  if (!user) return false;
  
  const corporateRoles = [
    SystemRole.SUPER_ADMIN,
    SystemRole.CORPORATE_ADMIN,
    SystemRole.CORPORATE_FINANCE,
    SystemRole.CORPORATE_OPERATIONS,
  ];
  
  return corporateRoles.includes(user.role);
}

/**
 * Check if user is franchise level (single franchisee visibility)
 */
export function isFranchiseUser(user) {
  if (!user) return false;
  
  const franchiseRoles = [
    SystemRole.FRANCHISE_OWNER,
    SystemRole.FRANCHISE_ADMIN,
    SystemRole.FRANCHISE_FINANCE,
  ];
  
  return franchiseRoles.includes(user.role);
}
